import { useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/ui-bits";
import NotFound from "./NotFound";

const CONTENT = {
  privacy: {
    label: "Legal",
    title: "Privacy notice",
    lede: "How Androgen Health collects, uses and protects personal information — including the special protections that apply to health data.",
    sections: [
      {
        h: "Who we are",
        p: "Androgen Health is a UK online specialist testosterone health service. Androgen Health services are provided by High Street Health Ltd t/a Ready Health, registered in England and Wales (Companies House 15044189), operating a CQC-registered private clinic at 22 High Street, Standish, Wigan WN6 0HL. The data controller for the purposes of UK GDPR is High Street Health Ltd, ICO registration ZB729645.",
      },
      {
        h: "What we collect",
        p: "Enquiry details you submit through this site (name, email, phone and message content); assessment and clinical information if you become a patient; and privacy-aware analytics that are designed so that symptom information, test results and other health data are never sent to marketing analytics platforms.",
      },
      {
        h: "How health information is protected",
        p: "Health data is treated as special-category data: stored securely with role-based access limited to the clinical team, retained under a documented retention policy, and processed under data protection agreements with any processors. Conversion tracking and analytics on this site follow data-minimisation principles for sensitive healthcare data.",
      },
      {
        h: "Your rights",
        p: "You can request access, correction or deletion of your personal information, and object to processing, by contacting us through the site. You also have the right to complain to the Information Commissioner's Office.",
      },
      {
        h: "Before launch",
        p: "This notice is completed, reviewed against UK GDPR and Data Protection Act requirements, and dated before the service opens.",
      },
    ],
  },
  terms: {
    label: "Legal",
    title: "Terms of service",
    lede: "The terms that govern use of this website and, if you become a patient, the Androgen Health service.",
    sections: [
      {
        h: "Use of this website",
        p: "Content on this site is for information only and is not medical advice. Nothing on this site advertises prescription-only medicines; treatment is described as a clinical pathway that begins with assessment.",
      },
      {
        h: "The service",
        p: "Androgen Health provides online clinical assessment, blood testing coordination, and — where clinically appropriate — ongoing testosterone replacement therapy under the care of GMC-registered clinicians. Treatment is always subject to individual clinical assessment, and TRT is not suitable for everyone.",
      },
      {
        h: "Fees",
        p: "Proposed launch pricing is shown on this site: Foundation (tablets) £99/month, Precision (creams or depot injections) £129/month and Specialist (premium injectables) £179/month, plus one-off assessment fees from £35 to £149. Final fees are confirmed before going live. Plans are monthly with a clear cancellation policy; no safety-critical test or review the clinician deems necessary is charged as an extra.",
      },
      {
        h: "Liability and governing law",
        p: "These terms are governed by the laws of England and Wales. Nothing in them limits liability that cannot be limited by law. Full terms, including complaints and cancellation procedures, are completed and reviewed by legal counsel before launch.",
      },
    ],
  },
};

export default function LegalPage({ kind }) {
  const c = CONTENT[kind];
  if (!c) return <NotFound />;
  return (
    <>
      <Seo title={`${c.title} | Androgen Health`} description={c.lede.slice(0, 158)} />
      <PageHero label={c.label} title={c.title} lede={c.lede} safety={false} />
      <section className="pb-24 px-5 sm:px-8" data-testid={`legal-${kind}`}>
        <div className="max-w-3xl mx-auto space-y-12">
          {c.sections.map((s, i) => (
            <Reveal key={s.h} delay={i * 0.04}>
              <article>
                <h2 className="font-display font-semibold text-2xl sm:text-3xl tracking-wide">{s.h}</h2>
                <p className="text-mute text-base leading-relaxed mt-4">{s.p}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
